import {
  NormalizedPlanPresentation,
  PlanReviewThread,
  PlanThreadInterrupt,
  ReviewEntity,
  ReviewGap,
  ResolvedPlanInterrupt,
} from "./types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const stringifyValue = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (value === undefined) {
    return undefined;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * The jobId the thread itself advertises, from the `configurable` written when
 * the job started the run. Authoritative, and resolves even on a device whose
 * registry never saw the job.
 */
export const resolveThreadJobId = (
  thread: Pick<PlanReviewThread, "configurable"> | null | undefined
): string | undefined => {
  const configurable = thread?.configurable;
  return asString(configurable?.jobId) ?? asString(configurable?.job_id);
};

/**
 * This device's registry entry for a plan thread. The registry is keyed by
 * jobId while the review route carries the reportId, so both are matched —
 * and an entry that hasn't seen a /jobStatus snapshot carrying its reportId
 * yet won't match at all, which is why callers fall back to
 * `resolveThreadJobId`.
 */
export const findJobForThread = <T extends { id: string; reportId?: string }>(
  threadId: string | undefined,
  jobs: ReadonlyArray<T>
): T | undefined =>
  threadId
    ? jobs.find((job) => job.id === threadId || job.reportId === threadId)
    : undefined;

export const resolvePlanThreadId = (
  routeThreadId?: string,
  searchParams?: URLSearchParams,
  locationState?: unknown
): string | undefined => {
  const state = locationState as
    | { threadId?: string; reportId?: string }
    | undefined;

  return (
    routeThreadId ||
    searchParams?.get("threadId") ||
    searchParams?.get("reportId") ||
    state?.threadId ||
    state?.reportId
  );
};

const normalizeInterrupt = (
  interrupt: PlanThreadInterrupt
): ResolvedPlanInterrupt | null => {
  if (!interrupt?.interruptId) {
    return null;
  }

  const payload = isRecord(interrupt.payload) ? interrupt.payload : undefined;
  const toolName =
    asString(interrupt.toolName) ||
    asString(payload?.toolName) ||
    "planner_interrupt";

  const toolArgs =
    interrupt.toolArgs !== undefined ? interrupt.toolArgs : payload?.toolArgs;
  const schema =
    interrupt.schema ??
    (isRecord(payload?.schema)
      ? (payload.schema as Record<string, unknown>)
      : undefined);
  const ts = asString(interrupt.ts) || asString(interrupt.createdAt) || "";

  return {
    ...interrupt,
    toolName,
    toolArgs,
    schema,
    ts,
  };
};

export const resolvePlanInterrupts = (
  thread: PlanReviewThread | null
): ResolvedPlanInterrupt[] => {
  if (!thread) return [];

  const rawInterrupts =
    Array.isArray(thread.pendingInterrupts) && thread.pendingInterrupts.length > 0
      ? thread.pendingInterrupts
      : thread.pendingInterrupt?.interruptId
        ? [thread.pendingInterrupt]
        : [];

  return rawInterrupts
    .map(normalizeInterrupt)
    .filter((interrupt): interrupt is ResolvedPlanInterrupt => Boolean(interrupt));
};

export const filterStalePlanInterrupts = (
  thread: PlanReviewThread | null,
  respondedInterruptId?: string | null
): ResolvedPlanInterrupt[] => {
  const interrupts = resolvePlanInterrupts(thread);
  if (!thread) return [];

  const currentTurn = typeof thread.turn === "number" ? thread.turn : undefined;
  const resumeReceivedAt = thread.pendingResume?.receivedAt
    ? Date.parse(thread.pendingResume.receivedAt)
    : undefined;

  return interrupts.filter((interrupt) => {
    if (respondedInterruptId && interrupt.interruptId === respondedInterruptId) {
      return false;
    }

    if (
      typeof currentTurn === "number" &&
      typeof interrupt.turn === "number" &&
      interrupt.turn < currentTurn
    ) {
      return false;
    }

    const interruptAt = interrupt.ts || interrupt.createdAt;
    if (resumeReceivedAt && interruptAt) {
      const interruptTime = Date.parse(interruptAt);
      if (!Number.isNaN(interruptTime) && interruptTime <= resumeReceivedAt) {
        return false;
      }
    }

    return true;
  });
};

export const selectPlanInterrupt = (
  interrupts: ResolvedPlanInterrupt[]
): ResolvedPlanInterrupt | null => {
  if (interrupts.length === 0) return null;

  return (
    interrupts.find((interrupt) => interrupt.toolName === "present_plan") ||
    interrupts[0]
  );
};

export const normalizePlanPresentation = (
  interrupt: ResolvedPlanInterrupt | null
): NormalizedPlanPresentation | null => {
  if (!interrupt) return null;

  const args = isRecord(interrupt.toolArgs) ? interrupt.toolArgs : undefined;
  const title =
    asString(args?.title) ||
    asString(args?.heading) ||
    asString(args?.name) ||
    "Narrative First Draft";
  const summary =
    asString(args?.summary) ||
    asString(args?.description) ||
    asString(args?.context);
  const markdown =
    asString(args?.markdown) ||
    asString(args?.planMarkdown) ||
    asString(args?.content) ||
    asString(args?.message) ||
    stringifyValue(args?.plan) ||
    stringifyValue(interrupt.toolArgs) ||
    "No First Draft content was provided.";

  const entities = normalizeEntities(args?.entities);
  const gaps = normalizeGaps(args?.gaps);

  return {
    title,
    summary,
    markdown,
    rawArgs: interrupt.toolArgs,
    ...(entities.length > 0 ? { entities } : {}),
    ...(gaps.length > 0 ? { gaps } : {}),
  };
};

const normalizeEntities = (raw: unknown): ReviewEntity[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is Record<string, unknown> => isRecord(item) && typeof item.label === "string")
    .map((item) => ({
      id: crypto.randomUUID(),
      category: String(item.category ?? "Other"),
      label: String(item.label),
      details: asString(item.details),
      confidence: (["high", "medium", "low"] as const).find((v) => v === item.confidence),
      source: (["visual", "verbal", "both", "dispatch"] as const).find((v) => v === item.source),
    }));
};

const normalizeGaps = (raw: unknown): ReviewGap[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is Record<string, unknown> => isRecord(item) && typeof item.description === "string")
    .map((item) => ({
      id: crypto.randomUUID(),
      category: String(item.category ?? "Other"),
      description: String(item.description),
      type: (["missing", "ambiguous", "multiple-options"] as const).find((v) => v === item.type) ?? "missing",
      options: Array.isArray(item.options) ? item.options.filter((o): o is string => typeof o === "string") : undefined,
    }));
};

export const parseReviewInput = (value: string): unknown => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
};
